/**
 * 腾讯防水墙（任务 6d-2）：srcdoc iframe 内加载官方 TJCaptcha.js，结果经
 * postMessage 回传（带一次性 token 防多实例串台）。思路对齐旧版
 * tencent_captcha_web.dart，代码独立实现。
 *
 * 容灾口径（与旧版/腾讯官方一致）：SDK 脚本加载失败（8s 超时或 onerror）时
 * 回传 `trerror_1001_<appId>_<ts>` 格式的降级票据——服务端对该形态放行，
 * 验证码服务挂掉不至于把登录整个堵死。
 */

export interface CaptchaResult {
  readonly ticket: string;
  readonly randstr: string;
}

export type CaptchaMessage =
  | { readonly kind: 'success'; readonly result: CaptchaResult }
  | { readonly kind: 'cancel' };

const MESSAGE_SOURCE = 'lhim-captcha';

/** 解析 iframe 回传的 message 数据；非本实例（source/token 不符）返回 undefined */
export function readCaptchaMessage(data: unknown, token: string): CaptchaMessage | undefined {
  let obj: unknown = data;
  if (typeof data === 'string') {
    try {
      obj = JSON.parse(data);
    } catch {
      return undefined;
    }
  }
  if (typeof obj !== 'object' || obj === null) return undefined;
  const r = obj as { source?: unknown; token?: unknown; event?: unknown; payload?: unknown };
  if (r.source !== MESSAGE_SOURCE || r.token !== token) return undefined;
  if (r.event === 'success' && typeof r.payload === 'object' && r.payload !== null) {
    const p = r.payload as { ticket?: unknown; randstr?: unknown };
    if (typeof p.ticket === 'string' && p.ticket !== '' && typeof p.randstr === 'string') {
      return { kind: 'success', result: { ticket: p.ticket, randstr: p.randstr } };
    }
  }
  if (r.event === 'cancel' || r.event === 'success') return { kind: 'cancel' };
  return undefined;
}

/** 内嵌进 <script> 的 JS 字符串字面量：JSON 转义 + `<` 转 <（防 </script> 截断注入） */
function jsString(value: string): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

/** iframe srcdoc：加载 TJCaptcha.js 并立即 show，结果 postMessage 给父窗口 */
export function buildCaptchaHtml(appId: string, token: string): string {
  const appIdJson = jsString(appId);
  const tokenJson = jsString(token);
  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
<title>人机验证</title>
<style>
html, body { width: 100%; height: 100%; margin: 0; overflow: hidden; background: transparent; }
</style>
</head>
<body>
<script>
var APP_ID = ${appIdJson};
var TOKEN = ${tokenJson};
var done = false;
var loadTimer = setTimeout(function () {
  if (typeof TencentCaptcha !== 'function') degrade();
}, 8000);

function post(event, payload) {
  parent.postMessage(JSON.stringify({
    source: ${JSON.stringify(MESSAGE_SOURCE)},
    token: TOKEN,
    event: event,
    payload: payload || {}
  }), '*');
}

function onCaptcha(res) {
  if (done) return;
  done = true;
  var p = res || {};
  if (p.ticket) post('success', { ticket: p.ticket, randstr: p.randstr || '' });
  else post('cancel', {});
}

// SDK 加载失败的降级票据（服务端容灾口径，格式勿改）
function degrade() {
  clearTimeout(loadTimer);
  onCaptcha({
    ret: 0,
    randstr: '@' + Math.random().toString(36).slice(2),
    ticket: 'trerror_1001_' + APP_ID + '_' + Math.floor(Date.now() / 1000)
  });
}

function init() {
  clearTimeout(loadTimer);
  if (typeof TencentCaptcha !== 'function') { degrade(); return; }
  try {
    new TencentCaptcha(String(APP_ID), onCaptcha, { userLanguage: 'zh-cn' }).show();
  } catch (e) {
    degrade();
  }
}
</script>
<script src="https://turing.captcha.qcloud.com/TJCaptcha.js" onerror="degrade()"></script>
<script>init();</script>
</body>
</html>`;
}

/** 一次性实例 token（防多实例/陈旧 iframe 消息串台，无安全诉求，Math.random 足够） */
export function newCaptchaToken(): string {
  return `tc-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}
